import { z } from "zod";
import { localDateSchema, moneySchema, uuidSchema } from "../common/primitives.js";
/**
 * Yıkama raporları (Prompt 18, ADR 0018 §16–17) — izin `wash.report.read`, istasyon kapsamlı. Tarihler İstanbul iş
 * günüdür (döngünün bitiş günü), aralık dahil ve en çok 366 gün. Para kuruş dizgesi (KDV HARİÇ ciro).
 */
const rangeQuery = {
    stationId: uuidSchema,
    from: localDateSchema,
    to: localDateSchema,
};
const refineRange = (value, context) => {
    if (value.from > value.to)
        context.addIssue({ code: "custom", path: ["to"], message: "Bitiş başlangıçtan önce olamaz" });
    const days = (Date.parse(value.to) - Date.parse(value.from)) / 86_400_000;
    if (days > 366)
        context.addIssue({ code: "custom", path: ["to"], message: "Aralık en çok 366 gün olabilir" });
};
export const WASH_REPORT_GROUPS = ["unit", "program"];
export const washSummaryQuerySchema = z
    .object({ ...rangeQuery, groupBy: z.enum(WASH_REPORT_GROUPS).default("program") })
    .superRefine(refineRange);
export const washSummaryRowSchema = z.object({
    /** Ünite ya da program kimliği; çözülemeyen / atanamayan satırlar için null ("Atanmamış"). */
    key: uuidSchema.nullable(),
    label: z.string(),
    count: z.number().int().describe("Makine çalışması sayısı"),
    posRevenue: moneySchema.describe("Kasadaki program ürünü satırları (iade düşülmüş), KDV hariç"),
    machineRevenue: moneySchema.describe("Makinenin kendi tahsilatı, KDV hariç"),
    revenue: moneySchema,
    chemicalCost: moneySchema.describe("Stok defterindeki tüketim hareketlerinin değeri"),
    waterCost: moneySchema,
    electricityCost: moneySchema,
    totalCost: moneySchema,
    margin: moneySchema,
    marginPercent: z.string().nullable().describe("Marj / ciro × 100 (2 ondalık); ciro 0 ise null"),
    waterM3: z.string().describe("Paylaştırılmış / tahmini su (m³, 3 ondalık)"),
    electricityKwh: z.string().describe("Paylaştırılmış / tahmini elektrik (kWh, 3 ondalık)"),
});
export const WASH_UTILITY_SOURCES = ["UNIT_METER", "STATION_METER", "ESTIMATE"];
export const washUtilitySourceSchema = z.object({
    utility: z.enum(["WATER", "ELECTRICITY"]),
    source: z.enum(WASH_UTILITY_SOURCES),
    meterId: uuidSchema.nullable(),
    meterName: z.string().nullable(),
    unitIds: z
        .array(uuidSchema)
        .describe("Bu kaynağın döngülerinin üniteleri (istasyon sayacında: kendi sayacı olmayan üniteler — sayacı istasyon sayacının altındaki ünitelerin tüketimi istasyon sayacından düşülür)"),
    cycleCount: z.number().int(),
    quantity: z
        .string()
        .describe("Sayaç biriminde (m³ / kWh) bu kaynağa düşen dönem tüketimi (paylaştırılan + paylaştırılamayan) ya da tahmin toplamı"),
    deductedQuantity: z
        .string()
        .nullable()
        .describe("İstasyon sayacında: sayacın ölçtüğünden düşülen, ALTINDAKİ ünite sayaçlarına düşen tüketim (ölçülen = quantity + bu)"),
    unitCost: z
        .string()
        .nullable()
        .describe("₺ / birim (onaylı sayaç faturalarından); yoksa null ve maliyet 0"),
    cost: moneySchema,
});
/**
 * Sayacın ölçtüğü ama çalışmalara PAYLAŞTIRILAMAYAN tüketim: dönemde sayacın kapsadığı çalışma yok (`NO_CYCLES`) ya da
 * çalışmaların hiçbirinde programın tahmini su / elektrik tüketimi (× tüketim katsayısı) sıfırdan büyük değil
 * (`NO_WEIGHTED_CYCLES` — ör. elektrik tüketimi 0 kWh girilmiş el yıkama hizmetleri). Tüketim eşit bölünmez; ünite /
 * program satırlarına girmez, RAPOR TOPLAMLARINA girer ve Türkçe uyarıyla bildirilir.
 */
export const WASH_UNALLOCATED_REASONS = ["NO_CYCLES", "NO_WEIGHTED_CYCLES"];
export const washUnallocatedUtilitySchema = z.object({
    utility: z.enum(["WATER", "ELECTRICITY"]),
    source: z.enum(["UNIT_METER", "STATION_METER"]),
    meterId: uuidSchema.nullable(),
    meterName: z.string().nullable(),
    unitIds: z.array(uuidSchema),
    cycleCount: z.number().int().describe("Sayacın kapsadığı (ağırlığı sıfır) çalışma sayısı"),
    quantity: z.string().describe("Paylaştırılamayan tüketim (m³ / kWh, 3 ondalık)"),
    unitCost: z.string().nullable(),
    cost: moneySchema,
    reason: z.enum(WASH_UNALLOCATED_REASONS),
    message: z.string().describe("Türkçe açıklama"),
});
export const washSummarySchema = z.object({
    stationId: uuidSchema,
    from: localDateSchema,
    to: localDateSchema,
    groupBy: z.enum(WASH_REPORT_GROUPS),
    rows: z.array(washSummaryRowSchema),
    /** Satırların toplamı + paylaştırılamayan su / elektrik (`unallocated`) — marj toplamı bu maliyeti de içerir. */
    totals: washSummaryRowSchema.omit({ key: true, label: true }),
    utilitySources: z.array(washUtilitySourceSchema),
    unallocated: z
        .array(washUnallocatedUtilitySchema)
        .describe("Çalışmalara paylaştırılamayan sayaç tüketimi — satırlarda yok, toplamlarda var"),
    /** STOCK_LEDGER: kimyasal maliyeti stok defterinden; THEORETICAL: MARKET kapalı — maliyet hesaplanmadı (0). */
    chemicalCostSource: z.enum(["STOCK_LEDGER", "THEORETICAL"]),
    warnings: z.array(z.string()),
});
export const washOccupancyQuerySchema = z
    .object({ ...rangeQuery, unitId: uuidSchema.optional() })
    .superRefine(refineRange);
export const washOccupancySchema = z.object({
    stationId: uuidSchema,
    unitId: uuidSchema.nullable(),
    from: localDateSchema,
    to: localDateSchema,
    unitCount: z.number().int(),
    /** [hafta günü 0 = Pazartesi … 6 = Pazar][saat 0–23] → çalışılan saniye (İstanbul saati). */
    seconds: z.array(z.array(z.number().int())),
    /** Aynı hücrenin doluluk yüzdesi: saniye / (o hücrenin dönemdeki saat sayısı × 3600 × ünite sayısı), 1 ondalık. */
    percent: z.array(z.array(z.string())),
});
export const washFaultsQuerySchema = washOccupancyQuerySchema;
export const washFaultsSchema = z.object({
    stationId: uuidSchema,
    from: localDateSchema,
    to: localDateSchema,
    totalSeconds: z.number().int(),
    items: z.array(z.object({
        unitId: uuidSchema,
        unitName: z.string(),
        totalSeconds: z.number().int(),
        count: z.number().int(),
        byCode: z.array(z.object({ faultCode: z.string(), seconds: z.number().int(), count: z.number().int() })),
    })),
});
//# sourceMappingURL=reports.js.map